import {
  StatusTrackerActivity,
  WireEvent,
  DebitCardOrderEvent,
} from '@ally/shared-status-tracker'

/**
 *  Checks if the first activity is the ONLY activity that matches provided key
 */
export const onlyFirstActivityMatches = (
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
  key: 'activityName' | 'status',
  value: string,
): boolean => {
  if (key === 'status') {
    return (
      activity1.statuses[0].status === value &&
      activity2.statuses[0].status !== value
    )
  }
  return activity1.activityName === value && activity2.activityName !== value
}

/**
 *  Checks if the second activity is the ONLY activity that matches provided key
 */
export const onlySecondActivityMatches = (
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
  key: 'activityName' | 'status',
  value: string,
): boolean => {
  if (key === 'status') {
    return (
      activity2.statuses[0].status === value &&
      activity1.statuses[0].status !== value
    )
  }
  return activity2.activityName === value && activity1.activityName !== value
}

/**
 * Sorts wire transfer activities based on priority of status (contactUsFlag and VerificationRequired)
 */
export function sortWireTransferWithWarnings(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activityName = 'WireTransfer'
  const status = 'VerificationRequired'

  const isFirstWireTransferOnly = onlyFirstActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const isSecondWireTransferOnly = onlySecondActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const isFirstVerificationRequiredOnly = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    status,
  )
  const isSecondVerificationRequiredOnly = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    status,
  )

  switch (true) {
    case isFirstWireTransferOnly:
      return -1
    case isSecondWireTransferOnly:
      return 1
    case (activity1.eventModelData as WireEvent).contactUsFlag &&
      !(activity2.eventModelData as WireEvent).contactUsFlag:
      return -1
    case !(activity1.eventModelData as WireEvent).contactUsFlag &&
      (activity2.eventModelData as WireEvent).contactUsFlag:
      return 1
    case isFirstVerificationRequiredOnly:
      return -1
    case isSecondVerificationRequiredOnly:
      return 1
    default:
      return 0
  }
}

/**
 * Sorts debit card activities based on priority of status (Ordered but not funded)
 */
export function sortDebitCardNeedsAttention(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activity1CurrentStatus = activity1.statuses[0]
  const activity2CurrentStatus = activity2.statuses[0]
  const activityName = 'DebitCardOrder'
  const isFirstDebitCardOnly = onlyFirstActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )

  const isSecondDebitCardOnly = onlySecondActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  switch (true) {
    case isFirstDebitCardOnly:
      return -1
    case isSecondDebitCardOnly:
      return 1
    // Needs Attention status
    case activity1CurrentStatus.status === 'Ordered' &&
      (activity1.eventModelData as DebitCardOrderEvent).isFunded === false &&
      (activity2CurrentStatus.status !== 'Ordered' ||
        (activity2CurrentStatus.status === 'Ordered' &&
          (activity2.eventModelData as DebitCardOrderEvent).isFunded === true)):
      return -1
    case activity1CurrentStatus.status !== 'Ordered' ||
      (activity1CurrentStatus.status === 'Ordered' &&
        (activity1.eventModelData as DebitCardOrderEvent).isFunded === true &&
        activity2CurrentStatus.status === 'Ordered' &&
        (activity1.eventModelData as DebitCardOrderEvent).isFunded === false):
      return 1
    default:
      return 0
  }
}

/**
 * Sorts debit card activities based on priority of status (Ordered, Shipped, Delivered, Activated)
 */
export function sortDebitCardOther(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activityName = 'DebitCardOrder'
  const isFirstOnlyDebitCard = onlyFirstActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const isSecondOnlyDebitCard = onlySecondActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const firstIsOnlyOrdered = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'Ordered',
  )
  const secondIsOnlyOrdered = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'Ordered',
  )
  const firstIsOnlyShipped = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'Shipped',
  )
  const secondIsOnlyShipped = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'Shipped',
  )
  const firstIsOnlyDelivered = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'Delivered',
  )
  const secondIsOnlyDelivered = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'Delivered',
  )
  const firstIsOnlyActivated = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'Activated',
  )
  const secondIsOnlyActivated = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'Activated',
  )

  switch (true) {
    case isFirstOnlyDebitCard:
      return -1
    case isSecondOnlyDebitCard:
      return 1
    // Needs Attention status
    case firstIsOnlyOrdered:
      return -1
    case secondIsOnlyOrdered:
      return 1
    case firstIsOnlyShipped:
      return -1
    case secondIsOnlyShipped:
      return 1
    case firstIsOnlyDelivered:
      return -1
    case secondIsOnlyDelivered:
      return 1
    case firstIsOnlyActivated:
      return -1
    case secondIsOnlyActivated:
      return 1
    default:
      return 0
  }
}

/**
 * Sorts debit card activities based on priority of status (Ordered, Shipped, Delivered, Activated)
 */
export function sortDebitCardWithWarnings(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activityName = 'DebitCardOrder'
  const firstIsOnlyDebitCard = onlyFirstActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const secondIsOnlyDebitCard = onlySecondActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const firstIsOnlyDelayed = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'Delayed',
  )
  const secondIsOnlyDelayed = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'Delayed',
  )
  const firstIsOnlyFailed = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'DeliveryFailed',
  )
  const secondIsOnlyFailed = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'DeliveryFailed',
  )
  switch (true) {
    case firstIsOnlyDebitCard:
      return -1
    case secondIsOnlyDebitCard:
      return 1
    case firstIsOnlyDelayed:
      return -1
    case secondIsOnlyDelayed:
      return 1
    case firstIsOnlyFailed:
      return -1
    case secondIsOnlyFailed:
      return 1
    default:
      return 0
  }
}

/**
 * Sorts check order activities based on priority of status
 */
export function sortCanceledWireTransfer(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activityName = 'WireTransfer'
  const firstIsOnlyWireTransfer = onlyFirstActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const secondIsOnlyWireTransfer = onlySecondActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )

  const firstIsOnlyCanceled = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'Canceled',
  )

  const secondIsOnlyCanceled = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'Canceled',
  )
  switch (true) {
    case firstIsOnlyWireTransfer:
      return -1
    case secondIsOnlyWireTransfer:
      return 1
    case firstIsOnlyCanceled:
      return -1
    case secondIsOnlyCanceled:
      return 1
    default:
      return 0
  }
}

/**
 * Sorts wire transfer activities based on priority of status (Requested, Reviewing, Processing )
 */
export function sortInProcessWireTransfer(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activityName = 'WireTransfer'
  const firstIsOnlyWireTransfer = onlyFirstActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const secondIsOnlyWireTransfer = onlySecondActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const firstIsOnlyRequested = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'Requested',
  )
  const secondIsOnlyRequested = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'Requested',
  )
  const firstIsOnlyReviewing = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'Reviewing',
  )
  const secondIsOnlyReviewing = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'Reviewing',
  )
  const firstIsOnlyProcessing = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'Processing',
  )
  const secondIsOnlyProcessing = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'Processing',
  )
  switch (true) {
    case firstIsOnlyWireTransfer:
      return -1
    case secondIsOnlyWireTransfer:
      return 1
    case firstIsOnlyRequested:
      return -1
    case secondIsOnlyRequested:
      return 1
    case firstIsOnlyReviewing:
      return -1
    case secondIsOnlyReviewing:
      return 1
    case firstIsOnlyProcessing:
      return -1
    case secondIsOnlyProcessing:
      return 1
    default:
      return 0
  }
}

/**
 * Sorts wire transfer activities based on priority of status (Sent)
 */
export function sortSentWireTransfer(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activityName = 'WireTransfer'
  const firstIsOnlyWireTransfer = onlyFirstActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const secondIsOnlyWireTransfer = onlySecondActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const firstIsOnlySent = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'Sent',
  )
  const secondIsOnlySent = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'Sent',
  )
  switch (true) {
    case firstIsOnlyWireTransfer:
      return -1
    case secondIsOnlyWireTransfer:
      return 1
    case firstIsOnlySent:
      return -1
    case secondIsOnlySent:
      return 1

    default:
      return 0
  }
}

/**
 * Sorts check order activities based on priority of status
 */
export function sortCheckOrder(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activity1CurrentStatus = activity1.statuses[0]
  const activity2CurrentStatus = activity2.statuses[0]
  const activityName = 'CheckOrder'
  const firstIsOnlyCheckOrder = onlyFirstActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const secondIsOnlyCheckOrder = onlySecondActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  switch (true) {
    case firstIsOnlyCheckOrder:
      return -1
    case secondIsOnlyCheckOrder:
      return 1
    case activity1CurrentStatus.status === 'InProcess' &&
      activity2CurrentStatus.status !== 'InProcess':
      return -1
    case activity1CurrentStatus.status !== 'InProcess' &&
      activity2CurrentStatus.status === 'InProcess':
      return 1
    case activity1.activityName === 'CheckOrder' &&
      activity1CurrentStatus.status === 'Shipped':
      return 1
    case activity2.activityName === 'CheckOrder' &&
      activity2CurrentStatus.status === 'Shipped':
      return -1
    default:
      return 0
  }
}

/**
 * Sorts ACH transfer activities based on priority of status
 */
export function sortACH(
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): number {
  const activity1CurrentStatus = activity1.statuses[0].status
  const activity2CurrentStatus = activity2.statuses[0].status
  const activityName = 'ACHTransfer'
  const firstIsOnlyACH = onlyFirstActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const secondIsOnlyACH = onlySecondActivityMatches(
    activity1,
    activity2,
    'activityName',
    activityName,
  )
  const firstIsOnlyInprocess = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'InProcess',
  )
  const secondIsOnlyInprocess = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'InProcess',
  )
  const firstIsOnlyCompleted = onlyFirstActivityMatches(
    activity1,
    activity2,
    'status',
    'Completed',
  )
  const secondIsOnlyCompleted = onlySecondActivityMatches(
    activity1,
    activity2,
    'status',
    'Completed',
  )
  switch (true) {
    case (activity1.activityName === 'CheckOrder' &&
      activity1CurrentStatus === 'InProcess') ||
      (activity2.activityName === 'CheckOrder' &&
        activity2CurrentStatus === 'InProcess'):
      return 0
    case firstIsOnlyACH:
      return -1
    case secondIsOnlyACH:
      return 1
    case firstIsOnlyInprocess:
      return -1
    case secondIsOnlyInprocess:
      return 1
    case firstIsOnlyCompleted:
      return -1
    case secondIsOnlyCompleted:
      return 1
    default:
      return 0
  }
}

const hasWireTransfer = (
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): boolean => {
  return [activity1, activity2].some(
    activity => activity.activityName === 'WireTransfer',
  )
}
const hasACHTransfer = (
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): boolean => {
  return [activity1, activity2].some(
    activity => activity.activityName === 'ACHTransfer',
  )
}

const hasDebitCardOrder = (
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): boolean => {
  return [activity1, activity2].some(
    activity => activity.activityName === 'DebitCardOrder',
  )
}

const hasWireTransferWithWarnings = (
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): boolean => {
  return [activity1, activity2].some(
    activity =>
      activity.activityName === 'WireTransfer' &&
      (activity.statuses[0].status === 'VerificationRequired' ||
        !!(activity.eventModelData as WireEvent).contactUsFlag),
  )
}

const hasDebitCardNeedsAttention = (
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): boolean => {
  return [activity1, activity2].some(
    activity =>
      activity.activityName === 'DebitCardOrder' &&
      (activity.eventModelData as DebitCardOrderEvent).isFunded === false &&
      activity.statuses[0].status === 'Ordered',
  )
}

const hasCanceledWireTransfer = (
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): boolean => {
  return [activity1, activity2].some(
    activity =>
      activity.activityName === 'WireTransfer' &&
      activity.statuses[0].status === 'Canceled',
  )
}

const hasDebitCardWithWarnings = (
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): boolean => {
  return [activity1, activity2].some(
    activity =>
      activity.activityName === 'DebitCardOrder' &&
      (activity.statuses[0].status === 'Delayed' ||
        activity.statuses[0].status === 'DeliveryFailed'),
  )
}

const hasWireWithInProcessStatus = (
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): boolean => {
  return [activity1, activity2].some(
    activity =>
      activity.activityName === 'WireTransfer' &&
      (activity.statuses[0].status === 'Requested' ||
        activity.statuses[0].status === 'Reviewing' ||
        activity.statuses[0].status === 'Reviewing' ||
        activity.statuses[0].status === 'Processing'),
  )
}

const hasCheckOrderInprocess = (
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): boolean => {
  return [activity1, activity2].some(
    activity =>
      activity.activityName === 'CheckOrder' && activity.status === 'InProcess',
  )
}

const hasCheckOrderNotInprocess = (
  activity1: StatusTrackerActivity,
  activity2: StatusTrackerActivity,
): boolean => {
  return [activity1, activity2].some(
    activity =>
      activity.activityName === 'CheckOrder' && activity.status !== 'InProcess',
  )
}

/**
 * Fetches all status tracker activity and returns an array
 * of status tracker activities ordered by business logic rules
 *
 * ORDER OF PRIORITY
 * Wire - contactUsFlag
 * Wire - Needs Attention
 * Debit Card - Needs Attention
 * Wire - Canceled (show if <2 days since canceled status, hide if ≥2 days)
 * Debit Card - Delayed
 * Debit Card - Not Delivered
 * Wire - all other statuses
 *        Requested
 *        Reviewing
 *        Processing
 * Debit Card - all other statuses
 *        Ordered
 *        Shipped
 *        Delivered
 *        Activated
 * Sent wires - (show if <2 days since sent status, hide if ≥2 days)
 * Check Order – Ordered
 * ACH – Processing
 * ACH - Delivered (show if <2 days since delivered status, hide if ≥2 days)
 * Check Order - Shipped (show if <7 days since shipped status hide if ≥7 days)
 */
export function sortActivities(
  activities: StatusTrackerActivity[],
): StatusTrackerActivity[] {
  const sortedActivities = activities.sort(
    (activity1: StatusTrackerActivity, activity2: StatusTrackerActivity) => {
      /**
       * Sorts activities based on activity name and status
       */

      // Wire - contactUsFlag, Needs Attention
      if (hasWireTransferWithWarnings(activity1, activity2)) {
        const sort = sortWireTransferWithWarnings(activity1, activity2)
        if (sort) {
          return sort
        }
      }

      // Debit Card - Needs Attention
      if (hasDebitCardNeedsAttention(activity1, activity2)) {
        const sort = sortDebitCardNeedsAttention(activity1, activity2)
        if (sort) {
          return sort
        }
      }
      // Wire - Canceled
      if (hasCanceledWireTransfer(activity1, activity2)) {
        const sort = sortCanceledWireTransfer(activity1, activity2)
        if (sort) {
          return sort
        }
      }

      // Debit Card - Delayed,Not Delivered
      if (hasDebitCardWithWarnings(activity1, activity2)) {
        const sort = sortDebitCardWithWarnings(activity1, activity2)
        if (sort) {
          return sort
        }
      }
      // Wire - Requested, Reviewing,Processing
      if (hasWireWithInProcessStatus(activity1, activity2)) {
        const sort = sortInProcessWireTransfer(activity1, activity2)
        if (sort) {
          return sort
        }
      }
      // Debit Card - Ordered, Shipped, Delivered, Activated
      if (hasDebitCardOrder(activity1, activity2)) {
        const sort = sortDebitCardOther(activity1, activity2)
        if (sort) {
          return sort
        }
      }
      // Wire - Sent (or remaining)
      if (hasWireTransfer(activity1, activity2)) {
        const sort = sortSentWireTransfer(activity1, activity2)
        if (sort) {
          return sort
        }
      }
      // CheckOrder - Inprocess
      if (hasCheckOrderInprocess(activity1, activity2)) {
        const sort = sortCheckOrder(activity1, activity2)
        if (sort) {
          return sort
        }
      }
      // ACH
      if (hasACHTransfer(activity1, activity2)) {
        const sort = sortACH(activity1, activity2)
        if (sort) {
          return sort
        }
      }
      // CheckOrder - Not Inprocess
      if (hasCheckOrderNotInprocess(activity1, activity2)) {
        const sort = sortCheckOrder(activity1, activity2)
        if (sort) {
          return sort
        }
      }

      return 0
    },
  ) as StatusTrackerActivity[]

  return sortedActivities
}
